import { getEmbeddedSessionToken } from '../embedded-auth';
import { captureApiError, captureApiException } from '../api-diagnostics';
import { resolveUrlOrgSlug } from '../subdomain';

const DEFAULT_API_URL =
  typeof window !== 'undefined' ? window.location.origin : 'http://localhost:8787';
export const API_URL = import.meta.env.VITE_API_URL || DEFAULT_API_URL;

// In the extension's embedded iframe the session cookie isn't delivered, so
// authenticate same-origin API calls with the Bearer session token instead.
// Gated to the API origin so the token never leaks to a third-party URL; a no-op
// for normal web sessions (no token → cookie auth, unchanged).
function isApiOrigin(input: RequestInfo | URL): boolean {
  try {
    const href =
      typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    const base = typeof window !== 'undefined' ? window.location.href : API_URL;
    return new URL(href, base).origin === new URL(API_URL).origin;
  } catch {
    return false;
  }
}

function withEmbeddedAuth(input: RequestInfo | URL, init?: RequestInit): RequestInit | undefined {
  const token = getEmbeddedSessionToken();
  if (!token || !isApiOrigin(input)) return init;
  const headers = new Headers(init?.headers);
  if (!headers.has('Authorization')) headers.set('Authorization', `Bearer ${token}`);
  return { ...init, headers };
}

/**
 * An API failure that retains the originating HTTP status.
 *
 * Callers used to get a bare `Error`, so every failure looked alike: TanStack
 * set `isError` for a 404 and a 500 identically, and a page keying its empty
 * state on that flag told people their organization did not exist whenever the
 * server was merely down.
 */
export class ApiError extends Error {
  readonly status: number;
  /**
   * The server's structured error code (`RATE_LIMITED`, `TIMEOUT`, …), when the
   * response carried one. Branch on THIS, never on the message: the server
   * sends a machine-readable code precisely so a rendering of the same failure
   * cannot drift into a new string per surface.
   */
  readonly code?: string;
  /** Whether the same call may succeed unchanged (the server's own verdict). */
  readonly retryable: boolean;
  /** Seconds the server asked us to wait (`Retry-After`). */
  readonly retryAfterSeconds?: number;

  constructor(
    message: string,
    status: number,
    options?: { code?: string; retryable?: boolean; retryAfterSeconds?: number }
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = options?.code;
    this.retryable = options?.retryable ?? false;
    this.retryAfterSeconds = options?.retryAfterSeconds;
  }
}

/** The server's public-read (or any other) rate limit told us to come back later. */
export function isRateLimitedError(error: unknown): error is ApiError {
  return error instanceof ApiError && (error.status === 429 || error.code === 'RATE_LIMITED');
}

/**
 * True when a request failed because the thing is not there (404). Narrow on
 * purpose: 403 is "exists, not yours", which warrants a different message.
 */
export function isNotFoundError(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}

/**
 * True when the SERVER failed, rather than the request being answered with a
 * refusal. Only 5xx and transport-level failures qualify.
 *
 * The distinction matters for empty states: a 401/403 means "log in" or "ask
 * for access" and has its own path through the UI, so lumping every non-404
 * into "something went wrong on our end" strands people on an error page
 * instead of sending them to sign in.
 */
export function isServerError(error: unknown): boolean {
  if (error instanceof ApiError) return error.status >= 500;
  if (!(error instanceof Error)) return false;
  // Not an `ApiError`, so the status did not survive. Recover it from the
  // `API error: <status>` message the older raw throws produced rather than
  // assuming a transport failure: reading a 401 as an outage is exactly the
  // bug this function exists to prevent, and it would come back silently the
  // next time a call site throws a plain `Error`.
  const status = /^API error: (\d{3})$/.exec(error.message)?.[1];
  if (status) return Number(status) >= 500;
  // No status anywhere: a genuine transport failure (offline, DNS, timeout)
  // never reached a server, which is an outage from the user's point of view.
  return true;
}

export async function apiErrorFor(
  response: Response,
  fallbackMessage?: string
): Promise<ApiError> {
  const fallback = fallbackMessage
    ? `${fallbackMessage}: ${response.status}`
    : `API error: ${response.status}`;
  const { message, code, retryable } = await readApiErrorBody(response, fallback);
  const retryAfter = Number(response.headers.get('Retry-After'));
  return new ApiError(message, response.status, {
    code,
    retryable,
    // Structure first: the server sends the wait as `Retry-After`. Only fall
    // back to the message's "Try again in Ns" for a 429 that predates it.
    retryAfterSeconds:
      Number.isFinite(retryAfter) && retryAfter > 0
        ? retryAfter
        : parseRetryAfterFromMessage(message),
  });
}

/**
 * Read the error envelope: `{ error, code?, retryable?, call_id? }` from the
 * REST handler, or `{ error_description }` from the auth middleware. The
 * message is for display; the code is for branching.
 */
async function readApiErrorBody(
  response: Response,
  fallback = `API error: ${response.status}`
): Promise<{ message: string; code?: string; retryable?: boolean }> {
  const raw = await response.text();
  if (!raw) return { message: fallback };

  try {
    const parsed = JSON.parse(raw) as {
      error?: unknown;
      error_description?: unknown;
      message?: unknown;
      code?: unknown;
      retryable?: unknown;
    };
    const message =
      (typeof parsed.message === 'string' && parsed.message.length > 0
        ? parsed.message
        : typeof parsed.error_description === 'string' &&
            parsed.error_description.length > 0
          ? parsed.error_description
          : typeof parsed.error === 'string' && parsed.error.length > 0
            ? parsed.error
            : null) ?? fallback;
    return {
      message,
      code: typeof parsed.code === 'string' ? parsed.code : undefined,
      retryable: typeof parsed.retryable === 'boolean' ? parsed.retryable : undefined,
    };
  } catch {
    return { message: raw };
  }
}

/** Last resort for a 429 that carries no `Retry-After` header. */
function parseRetryAfterFromMessage(message: string): number | undefined {
  const match = /try again in (\d+)s/i.exec(message);
  if (!match) return undefined;
  const seconds = Number(match[1]);
  return Number.isFinite(seconds) && seconds > 0 ? seconds : undefined;
}

export async function throwIfNotOk(res: Response, fallbackMessage: string): Promise<void> {
  if (res.ok) return;
  throw await apiErrorFor(res, fallbackMessage);
}

const DEFAULT_TIMEOUT_MS = 10_000;

// Combine multiple AbortSignals into one. Prefers the platform `AbortSignal.any`
// (Node 20.3+, modern browsers, jsdom 23+) and falls back to a tiny shim for
// environments that haven't shipped it yet.
function composeSignals(signals: AbortSignal[]): AbortSignal {
  if (signals.length === 1) return signals[0];
  if (typeof (AbortSignal as unknown as { any?: typeof AbortSignal.any }).any === 'function') {
    return AbortSignal.any(signals);
  }
  const controller = new AbortController();
  for (const s of signals) {
    if (s.aborted) {
      controller.abort(s.reason);
      break;
    }
    s.addEventListener(
      'abort',
      () => {
        controller.abort(s.reason);
      },
      { once: true }
    );
  }
  return controller.signal;
}

export async function fetchWithTimeout(
  input: RequestInfo | URL,
  rawInit?: RequestInit & { timeoutMs?: number }
) {
  const init = withEmbeddedAuth(input, rawInit) as
    | (RequestInit & { timeoutMs?: number })
    | undefined;
  const timeoutMs = init?.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const timeoutController = new AbortController();
  const timeout = setTimeout(
    () =>
      timeoutController.abort(
        new DOMException(`Request timed out after ${timeoutMs}ms`, 'TimeoutError')
      ),
    timeoutMs
  );

  // Compose the caller's signal (if any) with our timeout signal so external
  // cancellation still works — the previous implementation silently overwrote
  // `init.signal`, breaking callers that wanted to cancel mid-flight.
  const signal = init?.signal
    ? composeSignals([init.signal, timeoutController.signal])
    : timeoutController.signal;

  try {
    const response = await fetch(input, {
      ...init,
      signal,
    });
    if (!response.ok) {
      captureApiError(input, response, init);
    }
    return response;
  } catch (error) {
    // Timeout-driven aborts are expected — don't report them.
    const isAbort =
      timeoutController.signal.aborted ||
      init?.signal?.aborted === true ||
      (error instanceof DOMException && error.name === 'AbortError') ||
      (error instanceof Error && error.name === 'AbortError');
    if (!isAbort) {
      captureApiException(input, error, init);
    }
    throw error;
  } finally {
    clearTimeout(timeout);
  }
}

/**
 * Loopback single-user sign-in: mints a session for the install's single user
 * with no credential prompt. The server enforces the trust boundary (loopback
 * peer + the `X-Lobu-Client` header). Shared by the `/auth/login` flow and the
 * app-wide auto sign-in (`useLocalInstallAutoSignIn`).
 */
export async function postLocalInit(): Promise<Response> {
  return fetchWithTimeout(`${API_URL}/api/local-init`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Lobu-Client': 'web' },
    credentials: 'include',
    body: '{}',
  });
}

/**
 * Append a short-lived `?token=` SSE ticket to an EventSource URL so it
 * authenticates in the embedded iframe (where EventSource can't send an
 * Authorization header and there's no session cookie). A no-op for normal web
 * (no embedded token → the session cookie authenticates). Best-effort by
 * default; pass `throwOnFailure` for the agent chat stream, where opening an
 * unauthenticated stream that 403s is worse than surfacing a clear error.
 */
async function withSseTicket(
  sseUrl: string,
  opts?: { throwOnFailure?: boolean }
): Promise<string> {
  if (!getEmbeddedSessionToken()) return sseUrl;
  try {
    const res = await fetchWithTimeout(`${API_URL}/api/sse-ticket`, { timeoutMs: 10_000 });
    if (res.ok) {
      const { ticket } = (await res.json()) as { ticket?: string };
      if (ticket) {
        const url = new URL(sseUrl);
        url.searchParams.set('token', ticket);
        return url.toString();
      }
    }
  } catch {
    // fall through to the failure handling below
  }
  if (opts?.throwOnFailure) {
    throw new Error(
      'Could not authenticate the stream. Try reopening or re-pairing the extension.'
    );
  }
  return sseUrl;
}

/**
 * Open an authenticated SSE stream. Pairs the two things every SSE consumer must
 * get right and would otherwise duplicate: minting a fresh `?token=` ticket (so
 * embedded-iframe streams authenticate without a cookie) and opening with
 * `withCredentials: true` (so normal-web streams send the session cookie).
 * Lifecycle (reconnect, queueing, abort) stays with the caller — those differ
 * per consumer, so this deliberately does NOT own them.
 */
export async function openSseStream(
  sseUrl: string,
  opts?: { throwOnFailure?: boolean }
): Promise<EventSource> {
  const url = await withSseTicket(sseUrl, opts);
  return new EventSource(url, { withCredentials: true });
}

export type ApiOrgContext = {
  organizationId?: string | null;
  slug?: string | null;
};

/**
 * The org component of a cache key: one identity, never two.
 *
 * The same workspace reaches a hook as `{ slug }` from a URL-scoped caller and
 * as `{ slug, organizationId }` once `/api/organizations` or a record resolve
 * has supplied the id. Keying on both fields made those two shapes different
 * entries for the same request — measured as two identical
 * `manage_entity_schema get` calls on one record page. The slug is the stable
 * one (it is in the URL from first paint and uniquely names the org), so it
 * wins; the id is the fallback for account-scoped callers that have no slug.
 */
export function orgKey(ctx: {
  organizationId: string | null;
  slug: string | null;
}): string | null {
  return ctx.slug || ctx.organizationId;
}

export function normalizeOrgContext(orgContext?: ApiOrgContext | string | null): {
  organizationId: string | null;
  slug: string | null;
} {
  if (typeof orgContext === 'string') {
    return { organizationId: orgContext, slug: null };
  }
  return {
    organizationId: orgContext?.organizationId ?? null,
    slug: orgContext?.slug ?? null,
  };
}

export function getPathScope(): { slug: string | null } {
  return { slug: resolveUrlOrgSlug() };
}

/**
 * Resolve the org slug for an API call — explicit orgContext first, then the
 * URL path / subdomain scope — returning `null` when nothing is resolvable
 * yet. Non-throwing, so it's safe to call during render (e.g. to build a query
 * key). Use `resolveApiScope` when a slug is *required* (request URLs) and its
 * absence is a hard error.
 */
export function tryResolveApiScopeSlug(orgContext?: string | ApiOrgContext): string | null {
  if (typeof orgContext === 'string' && orgContext) {
    return orgContext;
  }

  if (orgContext && typeof orgContext !== 'string' && orgContext.slug) {
    return orgContext.slug;
  }

  return getPathScope().slug;
}

export function resolveApiScope(orgContext?: string | ApiOrgContext): {
  slug: string;
} {
  const slug = tryResolveApiScopeSlug(orgContext);
  if (slug) {
    return { slug };
  }

  throw new Error('Organization slug is required in URL scope');
}

export function resolveOrgSelector(ctx: { organizationId?: string | null; slug?: string | null }) {
  if (ctx.organizationId) {
    const pathScope = getPathScope();
    if (pathScope.slug) {
      return { slug: pathScope.slug };
    }
  }
  if (ctx.slug) {
    return { slug: ctx.slug };
  }
  throw new Error('Organization context required');
}

export async function apiCall<T>(
  endpoint: string,
  body: Record<string, unknown>,
  orgContext?: string | ApiOrgContext,
  options?: { timeoutMs?: number; signal?: AbortSignal }
): Promise<T> {
  const scope = resolveApiScope(orgContext);
  const response = await fetchWithTimeout(`${API_URL}/api/${scope.slug}/${endpoint}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(body),
    timeoutMs: options?.timeoutMs,
    signal: options?.signal,
  });

  if (!response.ok) {
    throw await apiErrorFor(response);
  }

  return response.json();
}
